import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Search, RefreshCw, X, ScrollText } from "lucide-react";
import { useAccountLogs } from "../../hooks/useAccountLogs";
import {
  STATUS_FILTER_ORDER,
  getStatusInfo,
} from "../../utils/statusLabels";
import {
  DateRangeSelector,
  StatusChip,
  ListSkeleton,
  EmptyState,
  computeDateRange,
} from "../_logFilters";
import BotMultiSelect from "./BotMultiSelect";
import LogRowWithBot from "./LogRowWithBot";

/**
 * "Toàn bộ log" — fullwidth page listing chatbot_log across the whole account.
 */
const AllLogsPage = ({
  bots = [],
  eventTypes = [],
  shopsList = [],
  chatbotType = "event",
}) => {
  const accountId = useSelector((state) => state.auth.user?.accountId);

  const [datePreset, setDatePreset] = useState("7d");
  const [customRange, setCustomRange] = useState({ dateFrom: "", dateTo: "" });
  const [statusSet, setStatusSet] = useState(() => new Set());
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedBotIds, setSelectedBotIds] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const dateRange = useMemo(() => {
    if (datePreset === "custom") {
      const dateFrom = customRange.dateFrom
        ? new Date(customRange.dateFrom).toISOString()
        : undefined;
      const dateTo = customRange.dateTo
        ? new Date(`${customRange.dateTo}T23:59:59`).toISOString()
        : undefined;
      return { dateFrom, dateTo };
    }
    return computeDateRange(datePreset);
  }, [datePreset, customRange]);

  const statusArr = useMemo(() => Array.from(statusSet), [statusSet]);

  const { rows, total, loading, loadingMore, error, hasMore, loadMore, refresh } =
    useAccountLogs(accountId, {
      status: statusArr.length > 0 ? statusArr : undefined,
      settingIds: selectedBotIds.length > 0 ? selectedBotIds : undefined,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
      search: search || undefined,
      chatbotType,
    });

  const toggleStatus = (s) => {
    setStatusSet((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const clearFilters = () => {
    setStatusSet(new Set());
    setSearchInput("");
    setSearch("");
    setDatePreset("7d");
    setCustomRange({ dateFrom: "", dateTo: "" });
    setSelectedBotIds([]);
  };

  const hasActiveFilter =
    statusSet.size > 0 ||
    search ||
    datePreset !== "7d" ||
    selectedBotIds.length > 0;

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Page header */}
      <header className="px-6 py-4 bg-white border-b border-gray-200 flex items-start gap-4">
        <div className="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow">
          <ScrollText size={26} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">
            Toàn bộ log chatbot
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Lịch sử tin nhắn của mọi bot trong tài khoản · {bots.length} bot ·{" "}
            {loading ? "đang đếm…" : `${total} tin trong phạm vi bộ lọc`}
          </p>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          {/* Sticky filter bar */}
          <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 border-b border-gray-200">
            <div className="px-6 py-3 space-y-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                <DateRangeSelector
                  datePreset={datePreset}
                  setDatePreset={setDatePreset}
                  customRange={customRange}
                  setCustomRange={setCustomRange}
                />
                <BotMultiSelect
                  bots={bots}
                  eventTypes={eventTypes}
                  value={selectedBotIds}
                  onChange={setSelectedBotIds}
                />

                <div className="ml-auto flex items-center gap-2">
                  <div className="text-xs text-gray-500">
                    {loading
                      ? "Đang tải…"
                      : `${rows.length}${total > rows.length ? `/${total}` : ""} tin`}
                  </div>
                  <button
                    type="button"
                    onClick={refresh}
                    disabled={loading}
                    className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100 disabled:opacity-50"
                    title="Tải lại"
                  >
                    <RefreshCw
                      size={12}
                      className={loading ? "animate-spin" : ""}
                    />
                    Làm mới
                  </button>
                </div>
              </div>

              {/* Status chips */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <StatusChip
                  label="Tất cả"
                  active={statusSet.size === 0}
                  onClick={() => setStatusSet(new Set())}
                />
                {STATUS_FILTER_ORDER.map((s) => {
                  const info = getStatusInfo(s);
                  const active = statusSet.has(s);
                  return (
                    <StatusChip
                      key={s}
                      label={info.label}
                      active={active}
                      dot={info.dot}
                      onClick={() => toggleStatus(s)}
                    />
                  );
                })}
              </div>

              {/* Search row */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search
                    size={13}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Tìm theo tên khách, nội dung tin nhắn..."
                    className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-gray-200 bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => setSearchInput("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
                {hasActiveFilter && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium px-2 py-1 rounded hover:bg-indigo-50"
                  >
                    Xóa bộ lọc
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* List */}
          <div className="px-6 py-4 space-y-2.5">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
                Lỗi tải log: {error}
              </div>
            )}

            {loading && rows.length === 0 ? (
              <ListSkeleton />
            ) : rows.length === 0 ? (
              <EmptyState
                hasFilter={hasActiveFilter}
                onClear={clearFilters}
                titleEmpty="Tài khoản chưa có log nào"
                descEmpty="Khi bot gửi tin nhắn đầu tiên, log sẽ xuất hiện ở đây."
              />
            ) : (
              <>
                {rows.map((log) => (
                  <LogRowWithBot
                    key={log.id}
                    log={log}
                    shopsList={shopsList}
                    eventTypes={eventTypes}
                  />
                ))}

                {hasMore && (
                  <div className="pt-3 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-200 rounded-md hover:bg-indigo-50 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {loadingMore && (
                        <RefreshCw size={14} className="animate-spin" />
                      )}
                      Tải thêm tin
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllLogsPage;
